import React from "react";

import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/storage";
import "firebase/compat/firestore";

import { getRecoil } from "recoil-nexus";
import Bugsnag from "@bugsnag/js";
import _ from "underscore";

import organizationIdSelector from "../atoms/organizationIdSelector";
import { nanoid } from "nanoid";
import { consola } from "consola";
import { useAdminDraftOptional } from "../context/AdminDraftContext";

export type QuestionsData = {
  id?: string;
  name: string;
  subTitle: string
  minValue: number
  maxValue: number
  isRequired: string
  tier1: string
  tier2: string
  tier3: string
  type: string
  index: number;
};

const useQuestions = (type = "daily") => {
  const organizationId = getRecoil(organizationIdSelector);
  const adminDraft = useAdminDraftOptional();
  const [liveQuestions, setLiveQuestions] =
    React.useState<QuestionsData[]>();
  const [loading, setLoading] = React.useState(true);

  const saveQuestions = React.useCallback(
    async function (newQuestions: any) {
      if (adminDraft) {
        if (type == "weekly") {
          adminDraft.updateWeeklyOptional(newQuestions);
        } else if (type == "daily") {
          adminDraft.updateOptional(newQuestions);
        }
        return;
      }

      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId);
      if (type == "weekly") {
        try {

          consola.debug("+++++ WRITE => useQuestions: saveWeeklyQuestion");
          await ref.set(
            {
              weeklyOptional: newQuestions,
            },
            { merge: true },
          );

        } catch (e) {
          console.error("DEBUG: Error saving weekly questions:", e);
          Bugsnag.notify(new Error("Error saving Report Question data"));
        }
      } else if (type == "daily") {
        try {

          consola.debug("+++++ WRITE => useQuestions: saveDailyQuestion");
          await ref.set(
            {
              optional: newQuestions,
            },
            { merge: true },
          );

        } catch (e) {
          console.error("DEBUG: Error saving daily questions:", e);
          Bugsnag.notify(new Error("Error saving Daily Question data"));
        }
      }
    },
    [organizationId, type, adminDraft],
  );

  const questions = React.useMemo(() => {
    if (!adminDraft) return liveQuestions;
    const wd = adminDraft.workingDraft;
    if (type === "weekly") {
      if (wd?.weeklyOptional !== undefined) {
        return _.sortBy(wd.weeklyOptional, "index");
      }
    } else if (type === "daily") {
      if (wd?.optional !== undefined) {
        return _.sortBy(wd.optional, "index");
      }
    }
    return liveQuestions;
  }, [adminDraft, liveQuestions, type]);

  const addQuestion = React.useCallback(
    async function (
      name: string,
      subTitle: string,
      minValue: number,
      maxValue: number,
      isRequired: string,
      tier1: string,
      tier2: string,
      tier3: string,
      type: string,
    ) {



      // Handle case where questions is null or undefined
      const questionsCopy = questions ? _.clone(questions) : [];


      questionsCopy.push({
        id: nanoid(),
        name: name,
        subTitle: subTitle,
        minValue: minValue,
        maxValue: maxValue,
        isRequired: isRequired,
        tier1: tier1,
        tier2: tier2,
        tier3: tier3,
        type: type,
        index: questionsCopy.length + 1,
      });



      await saveQuestions(questionsCopy);
    },
    [questions, saveQuestions],
  );

  // TODO: Deleting a question should activate a cloud function to remove references.
  const deleteQuestion = React.useCallback(
    async function (id: string) {
      if (questions) {
        const questionsCopy = _.clone(questions);
        const index = _.findIndex(questionsCopy, { id: id });
        if (index !== -1) {
          questionsCopy.splice(index, 1);

          // Reindex
          for (let i = 0; i < questionsCopy.length; i++) {
            questionsCopy[i].index = i + 1;
          }

          await saveQuestions(questionsCopy);
        }
      }
    },
    [organizationId, questions, saveQuestions],
  );

  const getQuestionById = React.useCallback(
    (id: string) => {
      if (!questions) {
        return null;
      }
      const value = _.findWhere(questions, { id: id });
      return value;
    },
    [questions],
  );

  const moveQuestion = React.useCallback(
    async (id: any, direction: any) => {
      if (!questions) {
        return;
      }

      if (direction === "up") {
        const index = _.findIndex(questions, { id });
        if (index !== -1) {
          if (index > 0) {
            // get prev index photo
            const prevQuestion = questions[index - 1];
            const currQuestion = questions[index];

            // Swap indexes
            const prevQuestionIndex = prevQuestion.index;
            prevQuestion.index = currQuestion.index;
            currQuestion.index = prevQuestionIndex;

            // Save them
            await saveQuestions(questions);
          }
        }
      } else {
        const index = _.findIndex(questions, { id });
        if (index !== -1) {
          if (index < questions.length - 1) {
            // get prev index photo
            const prevQuestion = questions[index + 1];
            const currQuestion = questions[index];

            // Swap indexes
            const prevQuestionIndex = prevQuestion.index;
            prevQuestion.index = currQuestion.index;
            currQuestion.index = prevQuestionIndex;

            // Save them
            await saveQuestions(questions);
          }
        }
      }
    },
    [questions, saveQuestions],
  );

  React.useEffect(() => {
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId);

    const subscription = ref.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> useQuestions");
      const data: any = snapshot.data();

      setLoading(false);
      if (!data) {
        setLiveQuestions([]);
      } else {
        if (type == "weekly") {
          const sorted = _.sortBy(data.weeklyOptional, "index");
          setLiveQuestions(sorted || []);
        } else if (type == "daily") {
          const sorted = _.sortBy(data.optional, "index");
          setLiveQuestions(sorted || []);
        }
      }
    }, (error) => {
      consola.error("useQuestions snapshot error:", error);
      setLoading(false);
      setLiveQuestions([]);
    });

    return () => {
      subscription();
    };
  }, [organizationId, type]);

  return {
    questions,
    loading,
    saveQuestions,
    deleteQuestion,
    moveQuestion,
    getQuestionById,
    addQuestion,
  };
};

export default useQuestions;
